/**
 * Pickup Order Service
 * 
 * Handles pickup orders which can contain multiple individual orders
 */

import axiosInstance from './axiosInstance';
import { getAccessToken } from './tokenService';

export interface PickupOrder {
  _id: string;
  pickupOrderId?: string;
  pharmacyId: string;
  pharmacyName?: string;
  status: 'assigned' | 'pickup-requested' | 'in-progress' | 'picked-up' | 'completed' | 'cancelled' | 'arrived-at-facility';
  orders: any[]; // Array of individual orders
  totalOrders: number;
  requestedAt: string;
  pickedUpAt?: string;
  completedAt?: string;
  notes?: string;
  rider?: any;
  driver?: any;
  isWaiting?: boolean | false;
  createdAt: string;
  updatedAt: string;
}

export interface PickupOrdersResponse {
  success: boolean;
  data: PickupOrder[];
  pagination?: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

export interface GetPickupOrdersParams {
  pharmacyId?: string;
  page?: number;
  limit?: number;
  status?: string;
}

export interface GetPickupOrderByIdParams {
  pickupOrderId: string;
  pharmacyId?: string;
}

export interface UpdatePickupOrderStatusParams {
  pickupOrderId: string;
  pharmacyId?: string;
  status: string;
  notes?: string;
}
class PickupService {
  private getBaseEndpoint(): string {
    // Get user role from token or localStorage
    const token = getAccessToken();
    if (!token) return '/pickup-orders'; // Default to pharmacy
    
    try {
      // Decode JWT token to get user role
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.isAdmin ? '/admin/pickup-orders' : '/pickup-orders';
    } catch (error) {
      console.warn('Failed to decode token, defaulting to pharmacy endpoint');
      return '/pickup-orders';
    }
  }
  /**
   * Get all pickup orders (admin or pharmacy based on user role)
   */
  async getPickupOrders(params?: GetPickupOrdersParams): Promise<PickupOrdersResponse> {
    try {
      const baseEndpoint = this.getBaseEndpoint();
      console.log('📦 PickupService: Fetching pickup orders from:', baseEndpoint, params);
      
      const queryParams = new URLSearchParams();
      if (params?.pharmacyId) queryParams.append('pharmacyId', params.pharmacyId);
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.status) queryParams.append('status', params.status);

      const queryString = queryParams.toString();
      const endpoint = queryString ? `${baseEndpoint}?${queryString}` : baseEndpoint;
      
      const response = await axiosInstance.get(endpoint);
      
      console.log('✅ PickupService: Pickup orders fetched', {
        count: response.data?.data?.length || 0,
        endpoint: endpoint
      });

      return {
        success: true,
        data: response.data?.data || response.data || [],
        pagination: response.data?.pagination
      };
    } catch (error: any) {
      console.error('❌ PickupService: Error fetching pickup orders:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch pickup orders');
    }
  }

  /**
   * Get specific pickup order with all its orders
   */
  async getPickupOrderById(params: GetPickupOrderByIdParams): Promise<{ success: boolean; data: PickupOrder }> {
    try {
      const baseEndpoint = this.getBaseEndpoint();
      console.log('📦 PickupService: Fetching pickup order by ID from:', baseEndpoint, params.pickupOrderId);
      
      const response = await axiosInstance.get(`${baseEndpoint}/${params.pickupOrderId}`);
      
      console.log('✅ PickupService: Pickup order fetched');

      return {
        success: true,
        data: response.data?.data || response.data
      };
    } catch (error: any) {
      console.error('❌ PickupService: Error fetching pickup order:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch pickup order');
    }
  }

  /**
   * Update pickup order status
   */
  async updatePickupOrderStatus(params: UpdatePickupOrderStatusParams): Promise<{ success: boolean; data?: PickupOrder; message?: string }> {
    try {
      const baseEndpoint = this.getBaseEndpoint();
      console.log('🔄 PickupService: Updating pickup order status from:', baseEndpoint, {
        pickupOrderId: params.pickupOrderId,
        status: params.status
      });
      
      const response = await axiosInstance.patch(
        `${baseEndpoint}/${params.pickupOrderId}/status`,
        {
          status: params.status,
          notes: params.notes
        }
      );
      
      console.log('✅ PickupService: Pickup order status updated');

      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'Status updated successfully'
      };
    } catch (error: any) {
      console.error('❌ PickupService: Error updating pickup order status:', error);
      throw new Error(error.response?.data?.message || 'Failed to update pickup order status');
    }
  }

  /**
   * Request pickup for multiple orders (creates a new pickup order)
   */
  async requestPickup(orderIds: string[]): Promise<{ success: boolean; data?: PickupOrder; message?: string }> {
    try {
      const baseEndpoint = this.getBaseEndpoint();
      console.log('📦 PickupService: Requesting pickup for orders from:', baseEndpoint, { count: orderIds.length });
      
      const response = await axiosInstance.post(baseEndpoint, {
        orderIds: orderIds
      });
      
      console.log('✅ PickupService: Pickup requested successfully');

      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message || 'Pickup requested successfully'
      };
    } catch (error: any) {
      console.error('❌ PickupService: Error requesting pickup:', error);
      throw new Error(error.response?.data?.message || 'Failed to request pickup');
    }
  }
}

// Create and export singleton instance
const pickupService = new PickupService();

// Export convenience functions
export const getPickupOrders = (params?: GetPickupOrdersParams) => 
  pickupService.getPickupOrders(params);

export const getPickupOrderById = (params: GetPickupOrderByIdParams) => 
  pickupService.getPickupOrderById(params);

export const updatePickupOrderStatus = (params: UpdatePickupOrderStatusParams) => 
  pickupService.updatePickupOrderStatus(params);

export const requestPickup = (orderIds: string[]) => 
  pickupService.requestPickup(orderIds);

export default pickupService;

